import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'src/redux/store';
import {
  setDashboardPdfLoader,
  setPdfReportData,
  setPdfReportDataLoader,
  setPdfReportSiteId,
  setLocalSeoReportData,
  resetPdfConfig
} from 'src/redux/dashboard';
import { setPdfPreviewLoader } from 'src/redux/siteAuditReducer';
import {
  setReduxFilteredLocationsData,
  setReduxLocationsData,
  setLocationsDetailsLoader
} from 'src/redux/localSeo';
import {
  getReportData,
  getLocalSeoReportData,
  getLocations
} from 'src/common/Scraper';
import { PDFSection } from '../types';
import { useLocation } from 'react-router';

/**
 * usePdfData Hook
 *
 * Orchestrates the data fetching required for the PDF Report.
 * It fetches all necessary data via a single call to getReportData.
 *
 * @param siteId - The ID of the current site being audited.
 * @param compare - The user-selected timeline ('current' or 'first').
 *
 * @returns
 * - loading: Boolean for overall fetching state.
 * - error: Any error message encountered.
 * - refetch: Function to manually trigger a data refresh.
 */
export const usePdfData = (
  siteId: string,
  compare: string,
  isPublic = false
) => {
  const location = useLocation();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [localSeoError, setLocalSeoError] = useState<string | null>(null);
  const [localSeoLoading, setLocalSeoLoading] = useState(false);

  const isPublicRoute = useMemo(() => {
    return location.pathname.includes(`seo-report`);
  }, [location]);

  // Core data from Redux
  const pdfReportData = useSelector(
    (state: RootState) => state.dashboard.pdfReportData
  );
  const pdfReportSiteId = useSelector(
    (state: RootState) => state.dashboard.pdfReportSiteId
  );
  const localSeoReportData = useSelector(
    (state: RootState) => state.dashboard.localSeoReportData
  );
  const pdfConfig = useSelector(
    (state: RootState) => state.dashboard.pdfConfig
  );

  const { reduxLocationsData } = useSelector(
    (state: RootState) => state.localSeo
  );

  const { siteAuditDetails } = useSelector(
    (state: RootState) => state.siteAudit
  ) as any;

  const selectedLocationId = useSelector(
    (state: RootState) => state.localSeo.selectedLocationId
  );

  // Locks and state tracking to prevent duplicate/concurrent calls
  const isFetchingRef = useRef(false);
  const isFetchingLocalSeoRef = useRef(false);
  const isFetchingLocationsRef = useRef(false);
  // Track which siteId we've already tried fetching Local SEO for to prevent infinite loops
  const triedLocalSeoSiteIdRef = useRef<string | null>(null);
  // Track which locationId and dates we currently have data for to detect changes
  const loadedLocationIdRef = useRef<string | null>(null);
  const loadedPeriodRef = useRef<string | null>(null);
  const loadedCompareRef = useRef<string | null>(null);

  const pdfReportDataLoader = useSelector(
    (state: RootState) => state.dashboard.pdfReportDataLoader
  );
  const locationsDetailsLoader = useSelector(
    (state: RootState) => state.localSeo.locationsDetailsLoader
  );

  // Use a Ref to keep track of current Redux state values inside stable callbacks
  // This prevents the callbacks from being re-created every time data arrives.
  const stateRef = useRef({
    pdfReportSiteId,
    pdfReportData,
    reduxLocationsData,
    localSeoReportData,
    pdfReportDataLoader,
    locationsDetailsLoader,
    selectedLocationId,
    siteAuditDetails,
    pdfConfig
  });

  useEffect(() => {
    stateRef.current = {
      pdfReportSiteId,
      pdfReportData,
      reduxLocationsData,
      localSeoReportData,
      pdfReportDataLoader,
      locationsDetailsLoader,
      selectedLocationId,
      siteAuditDetails,
      pdfConfig
    };
  }, [
    pdfReportSiteId,
    pdfReportData,
    reduxLocationsData,
    localSeoReportData,
    pdfReportDataLoader,
    locationsDetailsLoader,
    selectedLocationId,
    siteAuditDetails,
    pdfConfig
  ]);

  /**
   * Helper: ensureLocations
   * Fetches the location list once and stores it in Redux.
   */
  const ensureLocations = useCallback(async () => {
    const {
      reduxLocationsData: currentLocs,
      locationsDetailsLoader: currentLoading
    } = stateRef.current;

    // Skip if we already have location data (non-null & non-empty array),
    // or if a fetch is already in progress.
    if (
      (currentLocs !== null &&
        (!Array.isArray(currentLocs) || currentLocs.length > 0)) ||
      isFetchingLocationsRef.current ||
      currentLoading
    ) {
      return;
    }

    isFetchingLocationsRef.current = true;
    dispatch(setLocationsDetailsLoader(true));
    try {
      const locationsResp: any = await getLocations({});
      if (locationsResp?.status === 'success') {
        const locations = locationsResp.locations || [];
        dispatch(setReduxLocationsData(locations));
        const selectedLocations = locations.filter(
          (loc: any) => loc.selected === true && loc.temp !== true
        );
        dispatch(setReduxFilteredLocationsData(selectedLocations));
      }
    } catch (err) {
      console.error('getLocations failed in PDF Builder:', err);
    } finally {
      isFetchingLocationsRef.current = false;
      dispatch(setLocationsDetailsLoader(false));
    }
  }, [dispatch]);

  /**
  /**
   * Local SEO Specific Fetcher: fetchLocalSeo
   * Used for initial payload binding and reactive fetching when a section is enabled after initial load.
   */
  const fetchLocalSeo = useCallback(
    async (force = false) => {
      const {
        pdfConfig,
        pdfReportData,
        localSeoReportData: currentLocalData,
        pdfReportDataLoader: currentDataLoader
      } = stateRef.current;

      const committedLocationId = pdfConfig.localSeoOptions?.name;
      const configLocationIdFromServer =
        pdfReportData?.config?.local_seo_options?.name;

      const isLocationChanged =
        loadedLocationIdRef.current !== committedLocationId;
      const isDatesChanged =
        loadedPeriodRef.current !== pdfConfig.periodFilter ||
        loadedCompareRef.current !== pdfConfig.compareFilter;

      const somethingChanged =
        isLocationChanged || isDatesChanged || !currentLocalData;

      if (
        !siteId ||
        isFetchingLocalSeoRef.current ||
        (!force && currentLocalData && !somethingChanged)
      ) {
        return;
      }

    triedLocalSeoSiteIdRef.current = siteId;
    isFetchingLocalSeoRef.current = true;
    setLocalSeoLoading(true);
    setLocalSeoError(null);
    dispatch(setPdfPreviewLoader(true));

    try {
      if (!isPublic) await ensureLocations();

      // Determine if we should pass a specific locationId
      const configLocationIdFromServer =
        pdfReportData?.config?.local_seo_options?.name;

      // Only pass locationId if committed config differs from server baseline
      const locationIdToPass =
        committedLocationId &&
        committedLocationId !== configLocationIdFromServer
          ? committedLocationId
          : null;

      const localSeoResp: any = await getLocalSeoReportData(
        siteId,
        locationIdToPass
      );
      if (localSeoResp?.status === 'success' && localSeoResp?.response) {
        const localData = localSeoResp.response;
        // Determine whether to keep all keywords or truncate to first based on pdfConfig
        const includeAllKeywords =
          !!stateRef.current.pdfConfig?.includeAllKeywords ||
          !!stateRef.current.pdfReportData?.config?.local_seo_options
            ?.includeAllKeywords;
        // Only truncate to first keyword when includeAllKeywords is explicitly false
        if (
          !includeAllKeywords &&
          localData?.mapRank?.keywords &&
          localData.mapRank.keywords.length > 0
        ) {
          localData.mapRank.keywords = localData.mapRank.keywords;
        }
        dispatch(setLocalSeoReportData(localData));
        loadedLocationIdRef.current = committedLocationId;
        loadedPeriodRef.current = pdfConfig.periodFilter;
        loadedCompareRef.current = pdfConfig.compareFilter;
      } else {
        setLocalSeoError(
          localSeoResp?.message || "Couldn't get Local SEO details"
        );
      }
    } catch (localSeoErr) {
      console.error('Local SEO Data Fetching failed:', localSeoErr);
      setLocalSeoError("Couldn't get Local SEO details");
    } finally {
      isFetchingLocalSeoRef.current = false;
      setLocalSeoLoading(false);
      dispatch(setPdfPreviewLoader(false));
    }
  }, [siteId, dispatch, ensureLocations]);

  /**
   * Main Fetcher: fetchData
   * Orchestrates the primary report data fetching.
   */
  const fetchData = useCallback(
    async (force = false, silent = false) => {
      const state = stateRef.current;
      const {
        pdfReportSiteId: currentStoredId,
        pdfReportData: currentData,
        pdfReportDataLoader: currentLoading,
      } = state;

      if (!siteId || isFetchingRef.current || (currentLoading && !force)) {
        return;
      }

      isFetchingRef.current = true;
      const isNewSite = currentStoredId !== siteId;

      if (!silent || isNewSite) {
        setError(null);
        setLocalSeoError(null); // Reset local SEO error as well
        setLoading(true);
        dispatch(setPdfReportDataLoader(true));
        dispatch(setPdfPreviewLoader(true)); // Ensure preview loader is active
        // Reset config only on true site change or non-silent load
        if (isNewSite) {
          dispatch(resetPdfConfig());
          dispatch(setLocalSeoReportData(null));
          triedLocalSeoSiteIdRef.current = null;
        }
      }

      try {
        const committedLocationId = state.pdfConfig.localSeoOptions?.name;
        const configLocationIdFromServer =
          state.pdfReportData?.config?.local_seo_options?.name;

        const locationIdToPass =
          committedLocationId &&
          committedLocationId !== configLocationIdFromServer
            ? committedLocationId
            : null;

        const [resp, localSeoResp] = await Promise.all([
          getReportData(siteId) as Promise<any>,
          getLocalSeoReportData(siteId, locationIdToPass) as Promise<any>
        ]);

        if (resp?.status === 'success' && resp?.response) {
          dispatch(setPdfReportData(resp.response));
          dispatch(setPdfReportSiteId(siteId));

          if (resp.response?.config?.sections_visibility?.local_seo) {
            if (!isPublic) await ensureLocations();

            if (localSeoResp?.status === 'success' && localSeoResp?.response) {
              const localData = localSeoResp.response;
              const includeAllKeywords =
                !!state.pdfConfig?.includeAllKeywords ||
                !!resp.response.config.local_seo_options?.includeAllKeywords;

              if (
                !includeAllKeywords &&
                localData?.mapRank?.keywords &&
                localData.mapRank.keywords.length > 0
              ) {
                localData.mapRank.keywords = localData.mapRank.keywords;
              }
              dispatch(setLocalSeoReportData(localData));
              loadedLocationIdRef.current = committedLocationId;
              loadedPeriodRef.current = state.pdfConfig.periodFilter;
              loadedCompareRef.current = state.pdfConfig.compareFilter;
            } else if (localSeoResp?.status === 'error') {
              setLocalSeoError(
                localSeoResp?.message || "Couldn't get Local SEO details"
              );
            }
          }
        } else {
          setError(resp?.message || 'No data found for this report.');
        }
      } catch (err: any) {
        console.error('PDF data fetch failed:', err);
        setError(err?.message || 'Failed to fetch report data.');
      } finally {
        isFetchingRef.current = false;
        setLoading(false);
        dispatch(setPdfPreviewLoader(false));
        dispatch(setPdfReportDataLoader(false));
      }
    },
    [siteId, dispatch, ensureLocations, isPublic]
  );

  // Handle re-fetching whenever siteId or compare mode changes
  useEffect(() => {
    fetchData();
  }, [siteId, compare, fetchData]);

  // Reactively fetch Local SEO when:
  //  - The Local SEO section is first enabled (no data yet)
  //  - The COMMITTED location changes after "Save Changes" is clicked
  // NOTE: We intentionally do NOT include localSeoReportData in the dep array
  //       to prevent this effect from re-running every time localSeoReportData
  //       itself updates, which would cause duplicate API calls on business change.
  useEffect(() => {
    const {
      pdfConfig: currentConf,
      pdfReportData: currentReport,
      localSeoReportData: currentLocal,
      pdfReportDataLoader: currentDataLoader
    } = stateRef.current;

    // 1. Guard: Missing vital data or already busy
    if (
      !siteId ||
      !currentReport ||
      isFetchingRef.current ||
      isFetchingLocalSeoRef.current ||
      currentDataLoader
    ) {
      return;
    }

    // 2. Guard: Section disabled
    const isLocalSeoEnabled = currentConf.includedSections.includes(
      PDFSection.LOCAL_SEO
    );
    if (!isLocalSeoEnabled) return;

    // 3. Detect Divergence
    const committedLocationId = currentConf.localSeoOptions?.name;
    const committedPeriod = currentConf.periodFilter;
    const committedCompare = currentConf.compareFilter;

    const isLocationChanged =
      loadedLocationIdRef.current !== committedLocationId;
    const isDatesChanged =
      loadedPeriodRef.current !== committedPeriod ||
      loadedCompareRef.current !== committedCompare;

    // 4. Trigger only if data missing OR configuration changed
    if (!currentLocal || isLocationChanged || isDatesChanged) {
      fetchLocalSeo();
    }
  }, [
    siteId,
    // Only watch COMMITTED config changes.
    // Do NOT include loading states or fetchData/fetchLocalSeo here.
    pdfConfig.includedSections,
    pdfConfig.localSeoOptions?.name,
    pdfConfig.periodFilter,
    pdfConfig.compareFilter
  ]);

  // Cleanup Redux states on unmount
  useEffect(() => {
    return () => {
      dispatch(setPdfReportData(null));
      dispatch(setPdfReportSiteId(null));
      dispatch(setLocalSeoReportData(null));
      dispatch(resetPdfConfig());
    };
  }, [dispatch]);

  return {
    loading,
    error,
    localSeoError,
    localSeoLoading,
    refetch: (silent = false) => fetchData(true, silent),
    fetchLocalSeo
  };
};
